import { Chart, useChart } from "@chakra-ui/charts"
import { Bar, BarChart, CartesianGrid, LabelList, Legend, Tooltip, XAxis, YAxis } from "recharts"
import { Box, Heading, VStack } from "@chakra-ui/react"
import Informative from "@hydrologiq/shared/Informative"
import { unitTooltip } from "./ChartTooltip"
import { DEFAULT_FREQUENCY, type Frequency } from "./frequency"
import { reportDateFormatter } from "./reportDateFormat"
import { LABEL_TEXT_COLOR, PHASE_COLORS } from "./reportColors"
import { CAPTURE_CHART_TEXT_CSS } from "./chartHeaderTags"

/**
 * Strip the trailing "phase balance" wording from a phase graph title so the
 * chart heading isn't redundant with the "Phase balance" section it lives in.
 * "Load phase balance" → "Load", "Genset phase balance" → "Genset". A title
 * that is *only* "Phase balance" collapses to a neutral fallback.
 */
function stripPhaseBalance(title: string): string {
  const stripped = title.replace(/\s*phase balance\s*/i, " ").trim()
  return stripped.length > 0 ? stripped : "Phase"
}

/**
 * Structural types mirroring the generated `getReport` response shape
 * (`ReportGraph` / `ReportSeries` / `SeriesPoint` from
 * `@hydrologiq/gql-experience/graphql`). They are declared locally so this
 * component compiles ahead of GraphQL codegen for the new `getReport` query,
 * and remain structurally assignable from the generated types once emitted —
 * meaning `Report.tsx` can hand a `ReportGraph` straight in as `graph`.
 */
export interface PhaseSeriesPoint {
  time: string
  value?: number | null
  peak?: number | null
}

export interface PhaseSeries {
  key: string
  label?: string | null
  points: PhaseSeriesPoint[]
}

export interface PhaseBalanceGraph {
  title?: string | null
  axis?: string | null
  series: PhaseSeries[]
}

export interface PhaseBalanceChartProps {
  /** A `ReportGraph`-like object carrying the three phase-share series. */
  graph: PhaseBalanceGraph
  /** Chart heading; defaults to the graph title, then "Phase balance". */
  title?: string
  /**
   * Report cadence, so sub-daily buckets (e.g. an hourly-bucketed daily report)
   * get time-bearing tick/hover labels rather than a repeated day. Defaults to
   * {@link DEFAULT_FREQUENCY} for callers that don't thread it through.
   */
  frequency?: Frequency
}

/** The stacked datum key each phase series maps onto. */
type PhaseLeg = "l1" | "l2" | "l3"

/** Distinct colours + labels per phase leg of the stack (bottom→top). The
 * label carries the unit ("%") so the legend reads "L1 %" etc. */
const LEG_CONFIG: Record<PhaseLeg, { color: string; label: string }> = {
  l1: { color: PHASE_COLORS.l1, label: "L1 %" },
  l2: { color: PHASE_COLORS.l2, label: "L2 %" },
  l3: { color: PHASE_COLORS.l3, label: "L3 %" },
}

const LEG_ORDER: readonly PhaseLeg[] = ["l1", "l2", "l3"]

/**
 * Map a phase-share series key onto its stack leg. Handles BOTH the role-scoped
 * `phase_share_l{n}` fields and the class-scoped `genset_phase_share_l{n}`
 * fields (the "Genset phase balance" graph), since both are three-phase shares
 * differing only in source — the trailing `l1`/`l2`/`l3` selects the leg.
 */
function legForKey(key: string): PhaseLeg | null {
  if (key.endsWith("_l1")) return "l1"
  if (key.endsWith("_l2")) return "l2"
  if (key.endsWith("_l3")) return "l3"
  return null
}

const STACK_ID = "phase"

interface PhaseDatum {
  start: string
  l1?: number | null
  l2?: number | null
  l3?: number | null
}

/**
 * Fold the three phase-share series into one datum per time bucket:
 * `{ start, l1, l2, l3 }`. Absent phases stay `null` (rendered as a gap /
 * omitted segment) rather than being coerced to 0 — the reducer already
 * yields `null` for an absent phase.
 */
function toStackedData(series: PhaseSeries[]): PhaseDatum[] {
  const byBucket = new Map<string, PhaseDatum>()

  for (const s of series) {
    const leg = legForKey(s.key)
    if (!leg) continue
    for (const point of s.points) {
      const existing = byBucket.get(point.time) ?? { start: point.time }
      existing[leg] = point.value ?? null
      byBucket.set(point.time, existing)
    }
  }

  return Array.from(byBucket.values()).sort((a, b) => a.start.localeCompare(b.start))
}

export function PhaseBalanceChart({ graph, title, frequency = DEFAULT_FREQUENCY }: PhaseBalanceChartProps) {
  const data = toStackedData(graph.series ?? [])

  const chart = useChart<PhaseDatum>({
    data,
    series: LEG_ORDER.map((leg) => {
      const config = LEG_CONFIG[leg]
      return { name: leg, color: config.color, label: config.label, stackId: STACK_ID }
    }),
  })

  const dateFormatter = reportDateFormatter(frequency)

  const rawHeading = title ?? graph.title ?? "Phase balance"
  const heading = stripPhaseBalance(rawHeading)

  const hasData = data.some((datum) => datum.l1 != null || datum.l2 != null || datum.l3 != null)

  // Per-segment value labels, shown on screen and in the PDF. Suppressed for
  // dense windows where centring a number in each thin segment would collide
  // (3 legs × many buckets). Hidden for now to keep charts clean; to re-enable,
  // restore: data.length <= 16
  const showValueLabels = false

  return (
    <Box bg="card" boxShadow="card" borderRadius="card" p={4} width="100%" height="100%">
      <VStack pt={1} pb={2} gap={3} alignItems={"flex-start"} justifyContent="space-between" width="100%" height="100%">
        <Heading as="h3" size={"sm"}>
          {heading}
        </Heading>
        {!hasData ? (
          <Informative heading="No data available" text="No data available for the selected period." />
        ) : (
          <Chart.Root height="260px" chart={chart} css={CAPTURE_CHART_TEXT_CSS}>
            <BarChart data={chart.data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={chart.color("border.muted")} vertical={false} />
              {/* Keep a tick on the first and last buckets so the axis always
                  spans the full selected period (e.g. 00:00 → 23:00). */}
              <XAxis
                axisLine={false}
                tickLine={false}
                dataKey={chart.key("start")}
                tickFormatter={dateFormatter}
                interval="preserveStartEnd"
                tick={{ fill: LABEL_TEXT_COLOR }}
              />
              <Tooltip
                cursor={{ fill: chart.color("bg.muted") }}
                animationDuration={0}
                content={unitTooltip(graph.axis ?? "%", dateFormatter)}
              />
              {/* Hard 0–100 domain with fixed ticks. The three phase shares can
                  sum to 100.00000000000001 (float error), which recharts would
                  otherwise use as the axis max and print verbatim. Pinning the
                  ticks and rounding the label keeps the axis clean. */}
              <YAxis
                axisLine={false}
                tickLine={false}
                domain={[0, 100]}
                allowDataOverflow
                ticks={[0, 25, 50, 75, 100]}
                tick={{ fill: LABEL_TEXT_COLOR }}
                tickFormatter={(value) => `${Math.round(Number(value))} %`}
              />
              {chart.series.map((item) => (
                <Bar
                  key={item.name}
                  stackId={STACK_ID}
                  isAnimationActive={false}
                  dataKey={chart.key(item.name)}
                  fill={chart.color(item.color)}
                  name={String(item.label)}
                >
                  {/* Export-only per-segment share label, centred in each stack
                      segment. White text reads on the coloured phase fills.
                      `null`/absent legs render nothing. */}
                  {showValueLabels && (
                    <LabelList
                      dataKey={chart.key(item.name)}
                      position="center"
                      formatter={(value: string | number | boolean | null | undefined) =>
                        // Hide a 0 % segment label — the segment has no height to
                        // sit on, so the number would just float on the baseline.
                        // `Math.round` folds tiny sub-0.5 % shares to 0 too. No
                        // space before "%" so it stays on one line.
                        typeof value === "number" && Math.round(value) !== 0 ? `${Math.round(value)}%` : ""
                      }
                      style={{ fontSize: 9, fontWeight: 600, fill: "#ffffff" }}
                    />
                  )}
                </Bar>
              ))}
              <Legend
                verticalAlign="bottom"
                height={28}
                iconType="circle"
                iconSize={9}
                formatter={(value) => <span style={{ color: LABEL_TEXT_COLOR }}>{value}</span>}
              />
            </BarChart>
          </Chart.Root>
        )}
      </VStack>
    </Box>
  )
}

export default PhaseBalanceChart
